import { useAuth } from 'auth/hooks/useAuth';
import { useGetProfile } from 'auth/hooks/useGetProfile';
import { getQueryObj, getSharedPreferenceFromNative } from 'core-lib/utils/common';
import { isMGWebView, isParkPlusWebView, isUCDWebView } from 'core-lib/utils/source';
import { isServer, isStringValid, uuidGenerator } from 'core-lib/utils/utils';
import React from 'react';
import { useSelector } from 'react-redux';
import { PrimarySource } from '../../constants/Source';
import { Cookies, LocalStorage, SessionStorage } from '../../constants/storage';
import { getCookieValue, setCookieValue } from '../../utils/cookie';
import { getOsVersionFromNavigator, hasNativeBridge, isWebView } from '../../utils/helpers';
import { observeSessionKey } from '../../utils/eventTracking';
import { Thumbmark } from '@thumbmarkjs/thumbmarkjs';

export function ActivityRecorder() {
  const { isAuthenticated } = useSelector(state => state.auth);

  const { init: fetchProfile } = useGetProfile({});
  const { setAuthData } = useAuth();

  function removeAuthAndRefreshToken(searchQuery) {
    if (!searchQuery) return searchQuery;

    const paramsToRemove = ['auth_token', 'refresh_token'];

    const query = searchQuery.startsWith('?') ? searchQuery.slice(1) : searchQuery;

    const filteredQuery = query
      .split('&')
      .filter(param => {
        const [key] = param.split('=');
        return !paramsToRemove.includes(key);
      })
      .join('&');

    return filteredQuery ? `?${filteredQuery}` : '';
  }

  async function setOneTimeData() {
    if (!isServer()) {
      const query = getQueryObj();
      const getEventParam = suffix => {
        const key = Object.keys(query || {}).find(k => k.endsWith(suffix));
        return key ? query[key] : null;
      };
      if (
        !sessionStorage.getItem(SessionStorage.InitialPagePath) &&
        !sessionStorage.getItem(SessionStorage.InitialPageFullPath)
      ) {
        const url = new URL(global.window.location.href);
        const queryParams = removeAuthAndRefreshToken(url?.search);
        global.window.sessionStorage.setItem(SessionStorage.InitialPagePath, url.pathname);
        global.window.sessionStorage.setItem(
          SessionStorage.InitialPageFullPath,
          `${url.origin}${url.pathname}${queryParams}`,
        );
      }

      // set session id if user is coming first time
      if (!sessionStorage.getItem(SessionStorage.TrackingSessionId)) {
        sessionStorage.setItem(SessionStorage.TrackingSessionId, uuidGenerator());
      }

      // set anonymous id if user is coming first time
      if (!localStorage.getItem(LocalStorage.SegmentAnonymousId)) {
        localStorage.setItem(LocalStorage.SegmentAnonymousId, uuidGenerator());
      }

      if (!getCookieValue(Cookies.WebviewPlatform)) {
        const os = getOsVersionFromNavigator();
        setCookieValue(Cookies.WebviewPlatform, os);
      }
      if (
        getCookieValue(Cookies.PrimarySource) === PrimarySource.ParkPlusWebView &&
        !hasNativeBridge()
      ) {
        setCookieValue(Cookies.PrimarySource, '');
      }
      if (!getCookieValue(Cookies.PrimarySource)) {
        let src = isWebView() ? PrimarySource.ParkPlusWebView : '';
        setCookieValue(Cookies.PrimarySource, src);
      }

      if (isParkPlusWebView()) {
        if (!getCookieValue(Cookies.AccessToken)) {
          const accessToken = await getSharedPreferenceFromNative('auth_token');
          if (isStringValid(accessToken)) {
            setAuthData({ accessToken });
          }
        }
        if (!getCookieValue(Cookies.RefreshToken)) {
          const refreshToken = await getSharedPreferenceFromNative('refresh_token');
          if (isStringValid(refreshToken)) {
            setAuthData({ refreshToken });
          }
        }
      }

      if (!getCookieValue(Cookies.DeviceId)) {
        const tm = new Thumbmark();
        const result = await tm.get();
        const dId = result.thumbmark;
        setCookieValue(Cookies.DeviceId, dId);
      }

      const utmSource = query?.utm_source;
      const utmCampaign = query?.utm_campaign;
      const utmContent = query?.utm_content;
      const utmTerm = query?.utm_term;
      const adGroupid = query?.adgroupid;
      const utmMedium = query?.utm_medium;
      const clid = getEventParam('clid');
      const adCampaignid = getEventParam('ad_campaignid');
      const adSource = getEventParam('ad_source');
      const braid = getEventParam('braid');

      if (utmSource) {
        sessionStorage.setItem(SessionStorage.utmSource, utmSource);
      }
      if (utmCampaign) {
        sessionStorage.setItem(SessionStorage.utmCampaign, utmCampaign);
      }
      if (utmContent) {
        sessionStorage.setItem(SessionStorage.utmContent, utmContent);
      }
      if (utmTerm) {
        sessionStorage.setItem(SessionStorage.utmTerm, utmTerm);
      }
      if (adGroupid) {
        sessionStorage.setItem(SessionStorage.adGroupId, adGroupid);
      }
      if (utmMedium) {
        sessionStorage.setItem(SessionStorage.utmMedium, utmMedium);
      }

      if (clid) {
        sessionStorage.setItem(SessionStorage.clid, clid);
      }
      if (adCampaignid) {
        sessionStorage.setItem(SessionStorage.adCampaignid, adCampaignid);
      }
      if (adSource) {
        sessionStorage.setItem(SessionStorage.adSource, adSource);
      }
      if (braid) {
        sessionStorage.setItem(SessionStorage.braid, braid);
      }

      const trackingSessionId = sessionStorage.getItem(SessionStorage.TrackingSessionId);
      const anonymousId = localStorage.getItem(LocalStorage.SegmentAnonymousId);
      const sessionTracker: any = localStorage?.getItem(LocalStorage.SessionTracker)
        ? JSON.parse(localStorage.getItem(LocalStorage.SessionTracker))
        : {};
      const sessionId = sessionTracker?.session_id;
      const sessionStartTime = sessionTracker?.created_at?.toString();
      if (trackingSessionId && anonymousId && sessionId && sessionStartTime) {
        observeSessionKey();
      }

      window.onpopstate = event => {
        if (event) {
          // @ts-ignore
          window.popStateData = {
            isNavigatedFromLastPage: true,
            targetRoute: event?.path?.[0]?.location?.pathname,
          };
        }
      };
    }
  }
  function checkAndSetCallout() {
    if (!isServer() && (isParkPlusWebView() || isMGWebView() || isUCDWebView())) {
      const appRoot = global.document.getElementsByTagName('BODY')[0];
      if (!appRoot.classList.contains('cmn_no_callout')) appRoot.classList.add('cmn_no_callout');
    }
  }
  function setPadding() {
    if (!isServer()) {
      let checkCount = 6;
      const paddingTO = setInterval(() => {
        const pTop = getCookieValue('padding_top');
        const pBottom = getCookieValue('padding_bottom');
        // Only write when the native bridge has explicitly set the cookie.
        // In plain browser mode both cookies are absent so we skip this block,
        // letting the CSS env(safe-area-inset-*) fallback in global.scss apply.
        if (pTop != null && pBottom != null) {
          document.documentElement.setAttribute(
            'style',
            `--safe-area-top: ${pTop}px; --safe-area-bottom: ${pBottom}px`,
          );
        }
        checkCount += 1;
        if (checkCount > 6) {
          clearInterval(paddingTO);
        }
      }, 500);
    }
  }

  async function setProfile() {
    if (isParkPlusWebView()) {
      const profile = await getSharedPreferenceFromNative('user_data');
      if (profile) {
        setAuthData({ profile });
      }
    } else {
      fetchProfile();
    }
  }

  React.useEffect(() => {
    setOneTimeData();
    checkAndSetCallout();
    setPadding();
  }, []);

  React.useEffect(() => {
    if (!isServer() && isAuthenticated) {
      setProfile();
    }
  }, [isAuthenticated]);

  return null;
}
